import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { logout, api } from "@/lib/api";
import { Sparkles, LogOut, BookOpen, Trophy, Menu, Bell } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export const Navbar = () => {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/me/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  const openNotifs = async () => {
    if (notifs.unread > 0) {
      await api.post("/me/notifications/read").catch(() => {});
      setNotifs({ ...notifs, unread: 0 });
    }
  };

  const doLogout = async () => {
    await logout();
    setUser(null);
    nav("/");
  };

  return (
    <header
      data-testid="app-navbar"
      className="sticky top-0 z-40 border-b border-white/5 backdrop-blur-xl bg-[#07080A]/80"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Sparkles className="h-5 w-5 text-black" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-xl font-bold tracking-tight">ProSeCraft</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400/80 -mt-0.5">Stories, unbound</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link to="/companions" data-testid="nav-companions" className="px-3 py-2 text-sm text-rose-300 hover:text-rose-200 transition-colors">Companions</Link>
          <Link to="/reels" data-testid="nav-reels" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">Reels</Link>
          <Link to="/library" data-testid="nav-library" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">Library</Link>
          <Link to="/human-stories" data-testid="nav-human-stories" className="px-3 py-2 text-sm text-rose-300 hover:text-rose-200 transition-colors">Human Hearts</Link>
          <Link to="/stories" data-testid="nav-stories" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">All stories</Link>
          <Link to="/series" data-testid="nav-series" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">Series</Link>
          <Link to="/scenes" data-testid="nav-scenes" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">Scenes</Link>
          <Link to="/create" data-testid="nav-create" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">Create Free</Link>
          <Link to="/submit-companion" data-testid="nav-submit" className="px-3 py-2 text-sm text-slate-300 hover:text-rose-300 transition-colors">Submit Lead</Link>
          <Link to="/pricing" data-testid="nav-pricing" className="px-3 py-2 text-sm text-slate-300 hover:text-amber-300 transition-colors">Pricing</Link>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/gems" data-testid="credits-badge" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 hover:border-amber-400 transition">
                <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                <span className="font-mono text-xs font-semibold text-amber-300">{Intl.NumberFormat("en", { notation: "compact" }).format(user.credits || 0)}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber-400/70">gems</span>
              </Link>
              <DropdownMenu onOpenChange={(o) => o && openNotifs()}>
                <DropdownMenuTrigger asChild>
                  <button data-testid="notifs-btn" className="relative rounded-full p-2 hover:bg-white/5">
                    <Bell className="h-4 w-4 text-slate-300" />
                    {notifs.unread > 0 && (
                      <span data-testid="notifs-dot" className="absolute top-1 right-1 h-2 w-2 rounded-full bg-rose-500 shadow-[0_0_0_2px_#07080A]" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto bg-[#0F1117] border-white/10">
                  <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifs.items.length === 0 ? (
                    <div className="p-4 text-xs text-slate-500 text-center">Nothing new yet</div>
                  ) : notifs.items.slice(0, 12).map((n, i) => (
                    <button key={i} onClick={() => n.story_id && nav(`/story/${n.story_id}`)} className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-none">
                      <div className="text-xs text-slate-200">{n.message}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                    </button>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="rounded-full ring-1 ring-white/10 hover:ring-amber-400/60 transition">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-amber-500/20 text-amber-300 text-sm font-semibold">{user.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#0F1117] border-white/10">
                  <DropdownMenuLabel>
                    <div className="text-sm">{user.name}</div>
                    <div className="text-xs text-slate-400 font-normal">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}>
                    <BookOpen className="h-4 w-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-my-assets" onClick={() => nav("/my/assets")}>
                    <Sparkles className="h-4 w-4 mr-2" /> My art &amp; music
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-contest" onClick={() => nav("/contest")}>
                    <Trophy className="h-4 w-4 mr-2" /> Contest
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-support" onClick={() => nav("/support")}>
                    <Bell className="h-4 w-4 mr-2" /> Support
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>
                      <Trophy className="h-4 w-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={doLogout}>
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="inline-flex items-center justify-center bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-5 h-9">
              Sign in
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="mobile-menu-btn" className="md:hidden p-2 rounded-lg hover:bg-white/5">
                <Menu className="h-5 w-5 text-slate-300" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0F1117] border-white/10 md:hidden">
              <DropdownMenuItem onClick={() => nav("/reels")}>Reels</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/stories")}>Library</DropdownMenuItem>
              <DropdownMenuItem data-testid="mobile-nav-human-stories" onClick={() => nav("/human-stories")}>Human Hearts</DropdownMenuItem>
              <DropdownMenuItem data-testid="mobile-nav-write" onClick={() => nav("/write")}>Write a Story</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/series")}>Series</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/scenes")}>Scenes</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/create")}>Create Free</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/submit-companion")}>Submit Lead</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/contest")}>Contest</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/gems")}>Gems</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/pricing")}>Pricing</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
